import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { takeReturnTo } from '../lib/returnTo';
import { toast } from 'sonner';

const TermsAgreement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAgree = async () => {
    if (!agreed) {
      toast.error(t('auth.pleaseAgreeToTerms', 'Please agree to the Privacy Policy and Terms of Service'));
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Session expired. Please log in again.');
        navigate('/auth');
        return;
      }

      // Update profile with terms agreement timestamp
      const { error } = await supabase
        .from('profiles')
        .update({
          terms_agreed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('auth_user_id', session.user.id);

      if (error) {
        console.error('Error updating terms agreement:', error);
        toast.error('Failed to save agreement. Please try again.');
        return;
      }

      toast.success(t('auth.termsAgreed', 'Thank you for agreeing to our terms!'));

      // The last onboarding gate honors a remembered destination too (single-use):
      // an applicant who reached here still returns to the job they came for.
      const returnTo = takeReturnTo();
      window.location.href = returnTo || '/dashboard';
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {t('auth.termsTitle', 'Privacy Policy & Terms of Service')}
          </CardTitle>
          <CardDescription className="text-base">
            {t('auth.termsDescription', 'Please review and agree to our Privacy Policy and Terms of Service to continue using Bohol Trabaho. Your data is protected under the Philippine Data Privacy Act of 2012.')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Links to policies */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="/privacy"
              className="flex items-center justify-center gap-2 p-4 rounded-lg border hover:bg-muted transition-colors"
            >
              <FileText size={20} className="text-primary" />
              <span className="font-medium">{t('footer.privacy', 'Privacy Policy')}</span>
            </a>
            <a
              href="/terms"
              className="flex items-center justify-center gap-2 p-4 rounded-lg border hover:bg-muted transition-colors"
            >
              <FileText size={20} className="text-primary" />
              <span className="font-medium">{t('footer.terms', 'Terms of Service')}</span>
            </a>
          </div>

          {/* Agreement checkbox */}
          <div className="flex items-start space-x-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <Checkbox
              id="terms-agreement"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked)}
              className="mt-0.5"
            />
            <label htmlFor="terms-agreement" className="text-sm leading-relaxed cursor-pointer">
              <span className="text-blue-900 dark:text-blue-100">
                {t('auth.agreeToTerms', 'I have read and agree to the')}{' '}
              </span>
              <a href="/privacy" className="text-primary hover:underline font-medium">
                {t('footer.privacy', 'Privacy Policy')}
              </a>{' '}
              <span className="text-blue-900 dark:text-blue-100">{t('common.and', 'and')}</span>{' '}
              <a href="/terms" className="text-primary hover:underline font-medium">
                {t('footer.terms', 'Terms of Service')}
              </a>
            </label>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleAgree}
              className="w-full h-12 text-base font-semibold"
              disabled={!agreed || loading}
            >
              {loading ? t('common.loading', 'Loading...') : t('auth.agreeAndContinue', 'Agree & Continue')}
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-muted-foreground"
            >
              {t('auth.logout', 'Log out')}
            </Button>
          </div>

          {/* Note */}
          <p className="text-xs text-center text-muted-foreground">
            {t('auth.termsNote', 'By agreeing, you acknowledge that you have read and understood our policies regarding the collection, use, and protection of your personal data.')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsAgreement;
